import { useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { If } from 'src/components/If';
import { WalletInvitationLinkStatus } from 'src/services/invite-monitor';
import { getWalletsByGuestOwner } from 'src/services/wallet';
import { Wallet } from 'src/types/wallet';

import { SendWalletApprovalDialog } from './SendWalletApprovalDialog';
import { Filters } from '../Filters';
import { OwnerGuestWalletCard } from '../OwnerGuestWalletCard';

export function GuestPendingWalletTab({ ownerId }: { ownerId: string }) {
  const [searchName, setSearchName] = useState('');
  const [orderBy, setOrderBy] = useState('');
  const [openSendApprovalDialog, setOpenSendApprovalDialog] = useState(false);
  const [selectedWalletId, setSelectedWalletId] = useState<string | null>(null);
  const [selectedWalletName, setSelectedWalletName] = useState<string | null>(
    null,
  );

  const {
    data: wallets,
    isLoading: isLoadingWallets,
    isFetching: isFetchingWallets,
    refetch: refetchPendingWallets,
  } = useQuery({
    queryKey: ['pending-wallets', searchName, orderBy, ownerId],
    queryFn: () =>
      getWalletsByGuestOwner({
        ownerId,
        status: 'pending',
        filters: {
          name: searchName,
          sort: [orderBy],
        },
      }),
    onError: (error: Error) => {
      toast.error(error.message);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const handleOpenSendApprovalDialog = (
    walletId: string,
    walletName: string,
  ) => {
    setSelectedWalletId(walletId);
    setSelectedWalletName(walletName);
    setOpenSendApprovalDialog(true);
  };

  const handleCloseSendApprovalDialog = () => {
    setOpenSendApprovalDialog(false);
    setSelectedWalletId(null);
    setSelectedWalletName(null);
  };

  const isLoadingOrFetchingWallets = isLoadingWallets || isFetchingWallets;

  return (
    <>
      <Filters
        title=""
        searchEntityName="wallet"
        onSearch={(value) => setSearchName(value)}
        onOrderBy={(value) => setOrderBy(value)}
        orderBy={orderBy}
        useCriticalityFilters={false}
      />

      <If condition={isLoadingOrFetchingWallets}>
        <Box
          width="100%"
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      </If>

      <If condition={!wallets?.length && !isLoadingOrFetchingWallets}>
        <Box
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Typography variant="h5" color="GrayText">
            Não encontramos carteiras pendentes
          </Typography>
        </Box>
      </If>

      <If condition={wallets?.length && !isLoadingOrFetchingWallets}>
        <Box
          px={4}
          py={2}
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          flexWrap="wrap"
          alignItems="start"
          gap={1.5}>
          {wallets?.map(
            (
              wallet: Wallet & {
                walletInvitationLinkStatus: WalletInvitationLinkStatus;
              },
            ) => (
              <OwnerGuestWalletCard
                key={wallet.id}
                walletId={wallet.id.toString()}
                ownerId={ownerId}
                name={wallet.name}
                ownerName={wallet.owner.fullName}
                walletInvitationLinkStatus={wallet.walletInvitationLinkStatus}
                fundsCount={wallet.walletFunds.length}
                quotasCount={wallet.walletFunds.reduce(
                  (acc, fund) => acc + (fund.quotas?.length || 0),
                  0,
                )}
                onSendApproval={() =>
                  handleOpenSendApprovalDialog(
                    wallet.id.toString(),
                    wallet.name,
                  )
                }
              />
            ),
          )}
        </Box>
      </If>

      {selectedWalletId && selectedWalletName && (
        <SendWalletApprovalDialog
          open={openSendApprovalDialog}
          onClose={handleCloseSendApprovalDialog}
          walletId={selectedWalletId}
          walletName={selectedWalletName}
          onSuccess={refetchPendingWallets}
          invalidateQueries={[
            ['pending-wallets', searchName, orderBy, ownerId],
            ['guest-wallets-summary-info', ownerId, 'pending'],
          ]}
        />
      )}
    </>
  );
}
